'use client';

import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { usePostHog } from 'posthog-js/react';
import { Button } from '@/components/shared/Button';
import { fadeInUp, staggerContainer } from '@/lib/animations';
import { addUTMs } from '@/lib/utils';
import type { ProCtaContent, GlobalContent } from '@/lib/content-schema';

interface Props {
  content: ProCtaContent;
  global: GlobalContent;
}

export function ProCTA({ content, global: g }: Props) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const posthog = usePostHog();
  const ctaHref = addUTMs(g.dashboardUrl + '/register', 'pro_cta');

  return (
    <section id="contacto" className="py-24 md:py-32 bg-gradient-to-br from-zibarit-verde-oscuro to-zibarit-gris-oscuro">
      <motion.div
        ref={ref}
        variants={staggerContainer}
        initial="initial"
        animate={isInView ? 'animate' : 'initial'}
        className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
      >
        <motion.h2
          variants={fadeInUp}
          className="text-title text-3xl md:text-4xl lg:text-5xl text-zibarit-bone mb-6"
        >
          {content.title}
        </motion.h2>

        <motion.p
          variants={fadeInUp}
          className="text-body text-base md:text-lg text-zibarit-bone/80 max-w-xl mx-auto mb-10"
        >
          {content.description}
        </motion.p>

        {content.buttonVisible && (
          <motion.div variants={fadeInUp}>
            <Button
              href={ctaHref}
              target="_blank"
              rel="noopener noreferrer"
              magnetic
              size="lg"
              data-ph-capture
              onClick={() => posthog?.capture('landing_cta_clicked', { button_text: content.buttonText, destination_url: ctaHref, page: 'pro' })}
            >
              {content.buttonText}
            </Button>
          </motion.div>
        )}
      </motion.div>
    </section>
  );
}
